<script lang="ts">
	import type { CloudRecording } from '../types/cloud-recordings';
	import type { Device } from 'src/types/Device';
	import { onDestroy, onMount } from 'svelte';
	import { getCloudRecordings } from 'src/services/cam-cloud/recordings';
	import { Loader, Scroll, Stack, Text } from '@Shelly/shelly-design-system/index';
	import { format } from 'svelte-i18n';
	import { onCameraRecordingChange } from 'src/components/Camera/helpers/camera-thumbnail-watch';
	import { camera_storage_keys } from 'src/components/Camera/stores/camera_storage_keys';
	import { needsEncryptionSetup } from 'src/components/Camera/helpers/recording-key-management';
	import { routeToDeviceSettings } from 'src/stores/ui/route-to-device-settings';
	import RecordingsListItemV2 from './RecordingsTab/RecordingsListItemV2.svelte';
	import NoResults from './RecordingsTab/NoResults.svelte';
	import RecordingSetupCta from './RecordingsTab/RecordingSetupCta.svelte';
	import RecordingsLoadError from './RecordingsTab/RecordingsLoadError.svelte';

	export let device: Device;
	export let deviceId: string;

	const RECORDINGS_LIMIT = 3;

	let recordings: CloudRecording[] = [];
	let isLoading = true;
	let loadError = false;
	let showSetupCta = false;

	// Re-check when the local key backup changes (e.g. right after provisioning).
	$: {
		$camera_storage_keys;
		refreshSetupState(device);
	}

	const refreshSetupState = async (currentDevice: Device) => {
		showSetupCta = await needsEncryptionSetup(currentDevice);
	};

	const loadRecordings = async () => {
		// Recordings belong to the owner's account, so a guest has none to list.
		if (device?.shared) {
			isLoading = false;
			return;
		}
		loadError = false;
		try {
			const result = await getCloudRecordings(deviceId, { limit: RECORDINGS_LIMIT, offset: 0 });
			recordings = result.items as CloudRecording[];
		} catch (error) {
			console.log('Failed to load recordings', error);
			loadError = true;
		} finally {
			isLoading = false;
		}
	};

	const handleRetry = () => {
		loadError = false;
		loadRecordings();
	};

	const handleSelect = (event: CustomEvent<CloudRecording>) => {
		const recording = event.detail;
		routeToDeviceSettings.setTarget(deviceId, 'camera_recordings', 'view', recording as any);
	};

	onMount(loadRecordings);

	const unsubscribe = onCameraRecordingChange(deviceId, (recId) => {
		loadRecordings();
	});
	onDestroy(unsubscribe);
</script>

<div class="mt-l mb-m">
	<Text element="p" styleType="label-large" class="mb-s text-uppercase text-color-muted text-center">
		{$format('last_recordings_title', { default: 'Last Recordings' })}
	</Text>

	{#if device?.shared}
		<div class="pb-l">
			<NoResults
				noResultsFound={{
					icon: 'fa-light fa-lock',
					title: $format('recordings_list_shared_device', { default: 'Shared device' }),
					description: $format('recordings_list_shared_device_description', {
						default:
							'This camera is shared with you, so its recordings stay with its owner.',
					}),
				}}
			/>
		</div>
	{:else if isLoading}
		<div class="d-flex justify-content-center width-full p-m">
			<Loader />
		</div>
	{:else if loadError}
		<div class="pb-l">
			<RecordingsLoadError on:retry={handleRetry} />
		</div>
	{:else if showSetupCta && recordings.length === 0}
		<div class="pb-l">
			<RecordingSetupCta {deviceId} variant="empty" />
		</div>
	{:else if recordings.length > 0}
		{#if showSetupCta}
			<RecordingSetupCta {deviceId} variant="banner" />
		{/if}
		<Scroll smallerScroll>
			<Stack gap="s">
				{#each recordings as recording (recording.id)}
					<RecordingsListItemV2
						{recording}
						selected={false}
						variant="simple"
						on:select={handleSelect}
					/>
				{/each}
			</Stack>
		</Scroll>
	{:else}
		<div class="pb-l">
			<NoResults />
		</div>
	{/if}
</div>
